import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { passkitAPI } from '../../services/api';
import { logger } from '../../utils/logger';

interface EventEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  event: {
    id: string;
    name?: string;
    event_date: string;
    venue_name?: string;
    venue_id?: string;
  } | null;
  productionId: string;
}

interface Venue {
  id: string;
  name: string;
  uid: string;
}

const EventEditDialog: React.FC<EventEditDialogProps> = ({
  open,
  onClose,
  onSuccess,
  event,
  productionId
}) => {
  const [eventDate, setEventDate] = useState<Dayjs | null>(null);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingVenues, setLoadingVenues] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load venues when dialog opens
  useEffect(() => {
    if (open) {
      loadVenues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Initialize form when event prop changes
  useEffect(() => {
    if (event) {
      // Parse event date
      if (event.event_date) {
        const parsedDate = dayjs(event.event_date);
        setEventDate(parsedDate.isValid() ? parsedDate : null);
      }

      // Set venue if we have the data
      if (event.venue_id && event.venue_name) {
        setSelectedVenue({
          id: event.venue_id,
          name: event.venue_name,
          uid: ''
        });
      }
    }
  }, [event]);

  const loadVenues = async () => {
    try {
      setLoadingVenues(true);
      const response = await passkitAPI.listVenues(productionId);

      if (response.venues) {
        setVenues(response.venues);
      } else {
        setVenues([]);
      }
    } catch (err: any) {
      logger.error('Error loading venues:', err);
      setError('Greška pri učitavanju lokacija');
    } finally {
      setLoadingVenues(false);
    }
  };

  const handleSubmit = async () => {
    if (!event) return;

    // Validation - at least one field must be changed
    if (!eventDate && !selectedVenue) {
      setError('Morate promeniti datum ili lokaciju');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const updateData: any = {
        productionId: productionId  // Required by PassKit
      };

      if (eventDate) {
        updateData.scheduledStartDate = eventDate.toISOString();
      } else if (event.event_date) {
        // If not changing date, include current date
        updateData.scheduledStartDate = event.event_date;
      }

      if (selectedVenue) {
        updateData.venueId = selectedVenue.id;
      } else if (event.venue_id) {
        // If not changing venue, include current venue
        updateData.venueId = event.venue_id;
      }

      logger.log('🔧 Updating event:', {
        eventId: event.id,
        ...updateData
      });

      await passkitAPI.updateEvent(event.id, updateData);

      onSuccess();
      handleClose();
    } catch (err: any) {
      logger.error('Error updating event:', err);
      setError(err.response?.data?.detail || 'Greška pri izmeni događaja');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setEventDate(null);
    setSelectedVenue(null);
    setError(null);
    onClose();
  };

  if (!event) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Izmeni događaj</DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <DateTimePicker
            label="Datum i vreme *"
            value={eventDate}
            onChange={(newValue) => {
              // Handle both dayjs and Date objects
              if (newValue) {
                setEventDate(dayjs(newValue));
              } else {
                setEventDate(null);
              }
            }}
            slotProps={{
              textField: {
                fullWidth: true,
                required: false
              }
            }}
          />

          <Autocomplete
            options={venues}
            getOptionLabel={(option) => option.name}
            value={selectedVenue}
            onChange={(event, newValue) => setSelectedVenue(newValue)}
            loading={loadingVenues}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Lokacija"
                required={false}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingVenues ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <Alert severity="info">
            Možete promeniti datum/vreme i/ili lokaciju događaja.
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Otkaži
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || (!eventDate && !selectedVenue)}
        >
          {loading ? <CircularProgress size={24} /> : 'Sačuvaj izmene'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventEditDialog;
